import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ShieldCheck, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { itApi } from './itApi';

/**
 * §8.8 番号重複の解消 — まずドライラン、管理者が確認してから適用。
 * 残すのは技術資産。直す側だけに共通採番から新しい番号を発番する。
 */
export default function NumberDedupeCard({ isAdmin }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reprint, setReprint] = useState([]);
  const [deptPick, setDeptPick] = useState({});   // 行のキー → 部署コード（適用前に直せる）

  const load = () => itApi.numberDuplicates().then(setPlan).catch(() => setPlan(null));
  useEffect(() => { load(); }, []);

  const run = async (keys, confirmText) => {
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const out = await itApi.numberDuplicatesApply({ confirm: true, keys, dept_codes: deptPick });
      setReprint((prev) => [...out.applied.map((a) => ({
        id: a.id, old_code: a.old_code, new_code: a.new_code,
        ledger: a.ledger, name: a.name,
      })), ...prev]);
      toast.success(`${out.applied.length}件の番号を直しました`, {
        description: out.applied.map((a) => `${a.old_code} → ${a.new_code}`).join(' / '),
        duration: 15000,
      });
      (out.skipped || []).forEach((s) => toast.warning(s.reason, { duration: 10000 }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '解消できませんでした');
    }
    setBusy(false);
  };

  const apply = async () => {
    const fixable = (plan?.rows || []).filter((r) => r.fixable);
    await run(fixable.map((r) => r.key),
      `${fixable.length} 件の番号を直します。\n`
      + '重複は技術資産をそのまま残し、並びが逆の番号は 部署/種類（ENG/CSM…）に直します。\n'
      + '記号が抜けている番号は連番そのままで 部署コード を足します（カウンターは進めません）。\n'
      + '旧番号は「旧番号」として残り、旧コードで検索・スキャンしても当たります。'
      + '使用材料などの控えも新しい番号に書き換えます。\n実行しますか？');
  };

  const applyOne = async (row) => {
    const dept = deptPick[row.key] || row.dept_code;
    const proposed = row.reason === 'missing_dept'
      ? `${dept}/${String(row.proposed).split('/').pop()}` : row.proposed;
    await run([row.key], `${row.code} → ${proposed}\n${row.proposal}\n\nこの1件だけ直します。よろしいですか？`);
  };

  const undo = async (log) => {
    if (!window.confirm(`${log.new_code} を旧番号 ${log.old_code} に戻します。`
      + '\nこの行は「番号の直し」に再掲されます。実行しますか？')) return;
    setBusy(true);
    try {
      const out = await itApi.numberDuplicatesUndo(log.id);
      toast.success(`${out.was} を ${out.restored} に戻しました`, { description: out.note });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    }
    setBusy(false);
  };

  if (!plan) return null;
  const rows = plan.rows || [];
  const history = (plan.history || []).filter((h) => !h.undone_at);
  const reversed = rows.filter((r) => r.reason === 'reversed_prefix').length;
  const missing = rows.filter((r) => r.reason === 'missing_prefix').length;
  const missingDept = rows.filter((r) => r.reason === 'missing_dept').length;

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="it-report-dedupe">
      <h2 className="flex items-center gap-2 text-sm font-bold text-zinc-800">
        <Copy size={16} className="text-zinc-400" />番号の直し（重複・並びが逆・記号なし）
        <Badge variant="outline" className="ml-1 bg-zinc-50 text-[11px]"
          data-testid="it-report-dedupe-count">{rows.length}件</Badge>
        {reversed > 0 && (
          <Badge variant="outline" className="bg-amber-50 text-[11px] text-amber-800"
            data-testid="it-report-dedupe-reversed-count">並びが逆 {reversed}件</Badge>
        )}
        {missing > 0 && (
          <Badge variant="outline" className="bg-sky-50 text-[11px] text-sky-800"
            data-testid="it-report-dedupe-missing-count">記号なし {missing}件</Badge>
        )}
        {missingDept > 0 && (
          <Badge variant="outline" className="bg-teal-50 text-[11px] text-teal-800"
            data-testid="it-report-dedupe-missing-dept-count">部署コードなし {missingDept}件</Badge>
        )}
      </h2>

      {rows.length === 0 ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700"
          data-testid="it-report-dedupe-clean">
          <ShieldCheck size={14} />重複も並びの誤りも記号の抜けもありません
        </p>
      ) : (
        <>
          <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            これはドライランです（まだ何も変えていません・採番カウンターも進めていません）。
            重複は<b>技術資産をそのまま</b>残し、もう一方に共通採番から新しい番号を発番します。
            並びが逆の番号（種類/部署）は <b>部署/種類</b>（例 ENG/CSM00001）に採番し直します。
            技術資産で部署の記号が抜けている番号（例 CSM00001）は
            <b>連番をそのままに ENG/ を足すだけ</b>です（採番カウンターは進めません）。
            旧番号は「旧番号」として残るので、旧コードの検索・スキャンでも当たります。
            使用材料・製作依頼の材料・依頼明細に残った控えも新しい番号に書き換えます。
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="it-report-dedupe-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-[11px] text-zinc-500">
                  {['内容', '番号', '残す（変更なし）', '直す', '新しい番号（案）', ''].map((h, hi) => (
                    <th key={h || `act-${hi}`} className="pb-1.5 pr-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key} className="border-b border-zinc-100 align-top"
                    data-testid={`it-report-dedupe-row-${i}`}>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                        r.reason === 'reversed_prefix'
                          ? 'bg-amber-100 text-amber-800'
                          : r.reason === 'missing_prefix'
                            ? 'bg-sky-100 text-sky-800'
                            : r.reason === 'missing_dept'
                              ? 'bg-teal-100 text-teal-800' : 'bg-rose-100 text-rose-800'}`}
                        title={r.proposal}>
                        {r.reason_label}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 font-mono font-semibold" data-no-translate>{r.code}</td>
                    <td className="py-1.5 pr-3">
                      {r.keep ? (
                        <>
                          <span className="text-zinc-500">{r.keep.ledger}</span> {r.keep.name || '—'}
                          <span className="ml-1 font-mono text-[10px] text-zinc-400" data-no-translate>{r.keep.current}</span>
                        </>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className="text-zinc-500">{r.fix.ledger}</span> {r.fix.name || '—'}
                      <span className="ml-1 font-mono text-[10px] text-zinc-400" data-no-translate>{r.fix.current}</span>
                    </td>
                    <td className="py-1.5 pr-3 font-mono font-semibold text-teal-700" data-no-translate>
                      {r.reason === 'missing_dept' && r.fixable ? (
                        <span className="flex items-center gap-1">
                          <select value={deptPick[r.key] || r.dept_code}
                            onChange={(e) => setDeptPick((p) => ({ ...p, [r.key]: e.target.value }))}
                            className="h-7 rounded border border-zinc-200 bg-white px-1 font-sans text-[11px]"
                            data-testid={`it-report-dedupe-dept-${i}`}>
                            {(r.dept_choices || [r.dept_code]).map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <span>/{String(r.proposed).split('/').pop()}</span>
                        </span>
                      ) : (
                        r.proposed || <span className="font-sans text-rose-600">手で直してください</span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {isAdmin && r.fixable && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
                          onClick={() => applyOne(r)}
                          data-testid={`it-report-dedupe-fix-${i}`}>直す</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isAdmin && plan.fixable > 0 && (
            <Button size="sm" className="mt-3 bg-teal-700 hover:bg-teal-800" disabled={busy}
              onClick={apply} data-testid="it-report-dedupe-apply">
              {busy ? '処理中…' : `番号の直しを適用（${plan.fixable}件・管理者）`}
            </Button>
          )}        </>
      )}

      {reprint.length > 0 && (
        <div className="mt-4 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2"
          data-testid="it-report-dedupe-reprint">
          <p className="text-[11px] font-semibold text-teal-900">
            ラベル再印刷リスト（{reprint.length}件・旧番号 → 新番号）
          </p>
          <ul className="mt-1 space-y-0.5">
            {reprint.map((a) => (
              <li key={a.id} className="font-mono text-[11px] text-teal-900" data-no-translate>
                {a.old_code} → {a.new_code}
                <span className="ml-2 font-sans text-teal-700">{a.ledger}「{a.name || '—'}」</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <p className="text-[11px] font-semibold text-zinc-600">採番し直した履歴</p>
          <ul className="mt-1.5 space-y-1" data-testid="it-report-dedupe-history">
            {history.map((h) => (
              <li key={h.id} className="flex items-center gap-2 text-[11px] text-zinc-600">
                <span className="font-mono" data-no-translate>{h.old_code} → {h.new_code}</span>
                <span className="text-zinc-400">{h.ledger}「{h.name || '—'}」· {String(h.at).slice(0, 10)} · {h.by}</span>
                {isAdmin && (
                  <button type="button" onClick={() => undo(h)} disabled={busy}
                    className="ml-auto inline-flex items-center gap-1 text-teal-700 hover:underline"
                    data-testid={`it-report-dedupe-undo-${h.id}`}>
                    <ArrowCounterClockwise size={11} />元に戻す
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
